// components/DeviceDetail.tsx

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCog as FaCogIcon,
  FaThermometerHalf as FaThermometerHalfIcon,
  FaTint as FaTintIcon,
  FaBolt as FaBoltIcon,
  FaMicrochip as FaMicrochipIcon,
  FaYoutube as FaYoutubeIcon
} from 'react-icons/fa';
import { updateRecord, updateName, fetchDeviceByMac } from './DataHandler';
import './DeviceDetail.css';
import MQTTService from './MQTTService';
import jwtDecode from 'jwt-decode';
import ModalLed from './ModalLed';
import ModalSwitch from './ModalSwitch';
import ModalMeasure from './ModalMeasure';
import OttoPad from './OttoPad';
import OttoFirmwareFlash from './OttoFirmwareFlash';
import DeviceFirmwareFlash from './DeviceFirmwareFlash';

/* Icon Type Casting to fix build errors */
const FaCog = FaCogIcon as any;
const FaTemp = FaThermometerHalfIcon as any;
const FaHumi = FaTintIcon as any;
const FaBolt = FaBoltIcon as any;
const FaMicrochip = FaMicrochipIcon as any;
const FaYoutube = FaYoutubeIcon as any;

function isUpdateNeeded(currentVerStr: string, dbVerStr: string): boolean {
  if (!dbVerStr) return false;
  // 현재 버전 데이터가 없으면(이전 기기) 업데이트가 필요한 것으로 간주
  if (!currentVerStr || currentVerStr.trim() === "" || currentVerStr === "undefined") return true;
  const cleanCurrent = String(currentVerStr).replace(/[^0-9.]/g, '');
  const cleanDb = String(dbVerStr).replace(/[^0-9.]/g, '');

  const v1 = cleanCurrent.split('.').map(Number);
  const v2 = cleanDb.split('.').map(Number);

  const maxLength = Math.max(v1.length, v2.length);

  for (let i = 0; i < maxLength; i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;
    if (num1 < num2) return true;
    if (num1 > num2) return false;
  }
  return false;
}

interface DeviceDetailProps {
  mqttMessage: any;
}

interface Device {
  mac: string;
  name?: string[];
  temperature?: number;
  humidity?: number;
  in?: number[];
  out?: number[];
  [key: string]: any;
}

const DeviceDetail = ({ mqttMessage }: DeviceDetailProps) => {
  const { mac } = useParams<{ mac: string }>();
  const [device, setDevice] = useState<Device | null>(null);
  const [email, setEmail] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const [editedNames, setEditedNames] = useState<string[]>([]);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTargetIndex, setModalTargetIndex] = useState<number | null>(null);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [selectedSwitchIndex, setSelectedSwitchIndex] = useState<number | null>(null);
  const [editedSwitchLabel, setEditedSwitchLabel] = useState('');

  const [modalState, setModalState] = useState<{ isOpen: boolean; measurement?: { key: string; label: string } }>({
    isOpen: false,
  });

  const [isSavingName, setIsSavingName] = useState(false);
  const [nameUpdateError, setNameUpdateError] = useState('');

  // Update check
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode<{ email: string }>(token);
      setEmail(decodedToken.email);
    }
  }, []);

  useEffect(() => {
    if (mac) {
      fetchDeviceByMac(mac)
        .then((data) => {
          setDevice(data);
          setEditedNames(data.name || []);
        })
        .catch((error) => console.error('Error fetching device data:', error));
    }
  }, [mac]);

  useEffect(() => {
    if (mqttMessage && mac) {
      try {
        const parsedMessage = JSON.parse(mqttMessage);

        if (parsedMessage.mac === mac) {
          setDevice((prevDevice) => (prevDevice ? { ...prevDevice, ...parsedMessage } : parsedMessage));
          updateRecord(mac, parsedMessage).catch((error) => console.error('Error updating record:', error));
        }
      } catch (error) {
        console.error('Error parsing MQTT message:', error);
      }
    }
  }, [mqttMessage, mac]);

  useEffect(() => {
    // device.ver가 없더라도(이전 기기) 타입 정보가 있다면 업데이트 체크를 수행합니다.
    if (device && (device.type || device.t)) {
      try {
        const storedConfigsStr = localStorage.getItem('deviceConfigs');
        if (storedConfigsStr) {
          const storedConfigs = JSON.parse(storedConfigsStr);
          const configsObj = storedConfigs.firmwares ? storedConfigs.firmwares : storedConfigs;

          const deviceType = device.type || device.t;
          const latestVer = configsObj[deviceType];

          console.log(`[Version Check] Device MAC: ${device.mac}`);
          console.log(`[Version Check] Device Type: ${deviceType}, Current Ver: ${device.ver || 'Unknown'}, Latest Ver in DB: ${latestVer}`);

          if (latestVer) {
            // device.ver가 없으면 빈 문자열로 전달, isUpdateNeeded에서 true를 반환하게 됨
            const needsUpdate = isUpdateNeeded(String(device.ver || ""), String(latestVer));
            console.log(`[Version Check] Needs Update?: ${needsUpdate}`);
            if (needsUpdate) {
              setLatestVersion(String(latestVer));
              setIsUpdateAvailable(true);
            } else {
              setIsUpdateAvailable(false);
            }
          } else {
            console.log(`[Version Check] No latest version found in DB for type ${deviceType}`);
          }
        } else {
          console.log(`[Version Check] No deviceConfigs found in localStorage`);
        }
      } catch (e) {
        console.error('Error parsing deviceConfigs from localStorage', e);
      }
    }
  }, [device?.ver, device?.type, device?.t]);

  const handleSwitchToggle = (index: number) => {
    if (!device || !device.out) return;
    const newValue = !device.out[index];
    const fullMessage = {
      command: "setOutput",
      mac: device.mac,
      portNo: index,
      value: newValue ? 1 : 0
    };
    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, JSON.stringify(fullMessage));

    setDevice((prevDevice) => {
      if (!prevDevice) return null;
      const updatedOut = [...(prevDevice.out || [])];
      updatedOut[index] = newValue ? 1 : 0;
      return { ...prevDevice, out: updatedOut };
    });
  };

  const handleSave = async () => {
    if (!device) return;

    setIsSavingName(true);
    setNameUpdateError('');

    try {
      const response = await updateName(device.mac, editedNames);
      if (response?.message && response.message.toLowerCase().includes('not found')) {
        setNameUpdateError('기기를 찾을 수 없습니다. MAC을 확인하세요.');
        return;
      }

      setDevice({ ...device, name: editedNames });
      setEditMode(false);
    } catch (error) {
      console.error('Error updating record:', error);
      setNameUpdateError('이름 저장에 실패했습니다. 네트워크를 확인하고 다시 시도하세요.');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancel = () => {
    setEditedNames(device?.name || []);
    setEditMode(false);
  };

  const handleNameChange = (index: number, value: string) => {
    const updatedNames = [...editedNames];
    updatedNames[index] = value;
    setEditedNames(updatedNames);
  };

  // Modal Handlers
  const handleOpenModal = (index: number) => {
    setModalTargetIndex(index);
    setIsModalOpen(true);
  };

  const handleSwitchLabelClick = (index: number) => {
    if (device?.name) {
      const switchLabelIndex = index + 1 + (device.in?.length || 0);
      setEditedSwitchLabel(device.name[switchLabelIndex] || `스위치 ${index + 1}`);
    } else {
      setEditedSwitchLabel('');
    }
    setSelectedSwitchIndex(index);
    setIsSwitchModalOpen(true);
  };

  const handleOpenMeasureModal = (type: { key: string; label: string }) => {
    setModalState({ isOpen: true, measurement: type });
  };

  const handleCloseMeasureModal = () => {
    setModalState({ isOpen: false });
  };

  // 펌웨어는 무선(OTA)이 아니라 USB(COM 포트)로 설치한다. (DeviceFirmwareFlash)
  const deviceTypeVal = device?.type || device?.t;
  const flashType = typeof deviceTypeVal === 'string' && deviceTypeVal.startsWith('i2r-') ? deviceTypeVal : `i2r-0${deviceTypeVal}`;

  if (!device) return <div className="device-detail-container">Loading...</div>;

  return (
    <div className="device-detail-container" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="dashboard-header">
        <div className="device-title-area">
          <h2>
            {device.name && device.name[0] ? device.name[0] : 'Unnamed Device'}
            <FaCog className="edit-icon" onClick={() => setEditMode(true)} />
          </h2>
          <span style={{ color: '#64748b', fontSize: '0.9rem' }}>MAC: {device.mac}</span>
        </div>
        {(device.type || device.t) === 'i2r-otto' ? (
          <OttoFirmwareFlash /> // Otto: GitHub 에서 받아 USB(COM 포트)로 설치
        ) : (
          <DeviceFirmwareFlash type={flashType} /> // 그 외: 같은 방식으로 USB(COM 포트)로 설치
        )}
      </div>

      {/* 🚀 펌웨어 업데이트 알림 배너 */}
      {isUpdateAvailable && (
        <div style={{
          backgroundColor: '#fff9c4',
          border: '1px solid #fbc02d',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🚀</span>
            <div style={{ fontWeight: 'bold', color: '#856404' }}>
              현재 보드 버전은 {device?.ver || '알 수 없음'} 입니다. 새로운 펌웨어는 {latestVersion} 입니다.
            </div>
          </div>
          <div style={{ color: '#856404', fontSize: '15px', lineHeight: '1.5' }}>
            우측 상단의 파란색 <strong style={{ color: '#007bff' }}>[펌웨어 다운로드]</strong> 버튼을 눌러 기기를 USB 로 PC 에 연결해 최신 버전을 설치하세요. 설치하면 Wi-Fi 설정이 초기화되므로 설치 후 다시 입력해야 합니다.
          </div>
        </div>
      )}

      {editMode && (
        <div className="detail-card" style={{ marginBottom: '24px' }}>
          <h3>이름 변경</h3>
          <div className="edit-names-grid" style={{ display: 'grid', gridTemplateColumns: ((device?.type || device?.t) === 'i2r-otto') ? '1fr' : '1fr 1fr', gap: '10px' }}>
            {(() => {
              const inputCount = (device?.in && device.in.length > 0) ? device.in.length : 4;
              const outputCount = (device?.out && device.out.length > 0) ? device.out.length : 4;
              const isOtto = (device?.type || device?.t) === 'i2r-otto';
              const totalLength = isOtto ? 1 : 1 + inputCount + outputCount; // Otto 는 메인 이름만

              return Array.from({ length: totalLength }).map((_, index) => {
                let label;
                if (index === 0) label = "메인 기기 이름";
                else if (index <= inputCount) label = `입력 ${index}`;
                else {
                  const outIndex = index - inputCount;
                  label = `출력 ${outIndex}`;
                }

                return (
                  <div key={index} style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#666' }}>{label}</label>
                    <input
                      type="text"
                      value={editedNames[index] || ''}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                );
              });
            })()}
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={handleSave} className="action-btn btn-primary" disabled={isSavingName}>
              {isSavingName ? '저장 중...' : '저장'}
            </button>
            <button onClick={handleCancel} className="action-btn btn-secondary" disabled={isSavingName}>취소</button>
          </div>
          {nameUpdateError && (
            <div style={{ marginTop: '12px', color: '#dc2626', fontWeight: 'bold' }}>
              {nameUpdateError}
            </div>
          )}
        </div>
      )}

      {/* Otto 로봇: 조종 패드 */}
      {(device.type || device.t) === 'i2r-otto' && (
        <OttoPad mac={device.mac} email={email} mqttMessage={mqttMessage} />
      )}

      {/* 1. Input Status (Top) - Reordered */}
      {device.in && device.in.length > 0 && (
        <div className="detail-card status-panel" style={{ marginBottom: '24px' }}>
          <div className="card-title"><FaMicrochip /> 입력 상태 (Inputs)</div>
          <div className="status-grid-row">
            {device.in.map((state, index) => (
              <div key={`led-${index}`} className="status-item-col">
                <span className="status-name" onClick={() => handleOpenModal(index)}>
                  {device.name?.[index + 1] || `입력 ${index + 1}`}
                </span>
                <div className={`status-indicator ${state === 1 ? 'status-on' : 'status-off'}`}></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. Output Controls (Middle) - Reordered */}
      {device.out && device.out.length > 0 && (
        <div className="detail-card control-panel" style={{ marginBottom: '24px' }}>
          <div className="card-title"><FaBolt /> 출력 제어 (Outputs)</div>
          <div className="switches-grid">
            {device.out.map((state, index) => (
              <div key={`switch-${index}`} className="switch-item">
                <span className="switch-name" onClick={() => handleSwitchLabelClick(index)}>
                  {device.name && device.name[index + 1 + (device.in?.length || 0)]
                    ? device.name[index + 1 + (device.in?.length || 0)]
                    : `스위치 ${index + 1}`}
                </span>
                <button
                  className={`switch-toggle ${state === 1 ? 'active' : ''}`}
                  onClick={() => handleSwitchToggle(index)}
                ></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Sensor Grid (Bottom) - Reordered */}
      <div className="sensor-section">
        {device['temperature'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'temperature', label: '온도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}><FaTemp /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['temperature']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>°C</span></span>
              <span className="sensor-label">온도</span>
            </div>
          </div>
        )}
        {device['humidity'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'humidity', label: '습도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}><FaHumi /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['humidity']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>%</span></span>
              <span className="sensor-label">습도</span>
            </div>
          </div>
        )}
        {device['light'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'light', label: '조도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fef9c3', color: '#eab308' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['light']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>lx</span></span>
              <span className="sensor-label">조도</span>
            </div>
          </div>
        )}
        {device['analog'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'analog', label: 'Analog' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#f3f4f6', color: '#64748b' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['analog']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}></span></span>
              <span className="sensor-label">Analog</span>
            </div>
          </div>
        )}
        {device['carbon'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'carbon', label: 'CO2' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#d1fae5', color: '#10b981' }}><FaMicrochip /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['carbon']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>ppm</span></span>
              <span className="sensor-label">CO2</span>
            </div>
          </div>
        )}
        {/* voltAdc, voltBat: i2r-04, i2r-04-hmi 에서만 표시 */}
        {(device.type || device.t || '').match(/^i2r-04(-hmi)?$/) && device['voltAdc'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'voltAdc', label: '전압(ADC)' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#ede9fe', color: '#8b5cf6' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['voltAdc']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>V</span></span>
              <span className="sensor-label">전압(ADC)</span>
            </div>
          </div>
        )}
        {(device.type || device.t || '').match(/^i2r-04(-hmi)?$/) && device['voltBat'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'voltBat', label: '배터리' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fae8ff', color: '#d946ef' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['voltBat']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>V</span></span>
              <span className="sensor-label">배터리</span>
            </div>
          </div>
        )}
      </div>


      {/* Footer Links (Full Width) */}
      <div className="footer-links" style={{ marginTop: '40px' }}>
        <a href="https://youtu.be/cczPgCb1z8s" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
          <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 이름설정
        </a>
        {/* Otto 에는 타이머/입력트리거/센서트리거가 없으므로 다른 기기에서만 보여준다 */}
        {(device.type || device.t) !== 'i2r-otto' && (
          <>
            <a href="https://youtu.be/UIoOG9Iwc24" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
              <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 타이머설정
            </a>
            <a href="https://youtu.be/m2ZyhRWnZ0o" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
              <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 입력트리거
            </a>
            <a href="https://youtu.be/doaUzRg5nQU" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
              <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 센서트리거
            </a>
          </>
        )}
      </div>


      {/* Modals */}
      {isModalOpen && modalTargetIndex !== null && (
        <ModalLed
          mqttMessage={mqttMessage}
          onCancel={() => setIsModalOpen(false)}
          labelNo={modalTargetIndex}
          deviceMac={mac || 'Unknown MAC'}
          name={device.name || []}
        />
      )}

      {isSwitchModalOpen && selectedSwitchIndex !== null && (
        <ModalSwitch
          mqttMessage={mqttMessage}
          deviceMac={mac || 'Unknown MAC'}
          onCancel={() => setIsSwitchModalOpen(false)}
          labelNo={selectedSwitchIndex}
          currentLabel={editedSwitchLabel}
        />
      )}

      {modalState.isOpen && modalState.measurement && (
        <ModalMeasure
          typeSensor={modalState.measurement.key}
          value={device[modalState.measurement.key]}
          label={modalState.measurement.label}
          deviceMac={device.mac}
          mqttMessage={mqttMessage}
          onCancel={handleCloseMeasureModal}
        />
      )}
    </div>
  );
};

export default DeviceDetail;
